import { SiweMessage } from 'siwe';
import {
  Chain,
  checksumAddress,
  publicActions,
  WalletClient,
  createWalletClient,
  custom,
  EIP1193Provider,
  http,
  fallback,
} from 'viem';
import { ExtendedWalletClient } from '@sdk/auth/types';
import NotificationClient from '@sdk/notification';
import { SettingsService } from '@sdk/settings/settings.service';
import { AutoTraderService } from '@sdk/auth/autotrader.service';
import { AxiosInstance } from 'axios';
import { BackendAccount } from '@sdk/shared/types/settings.types';
import { createProtectedAxios } from '@sdk/utils/axios';
import { IxEventType } from '@sdk/notification/types';
import { toAccount } from 'viem/accounts';
import type EthereumProvider from '@walletconnect/ethereum-provider';
import { IxChain } from '@shared/src/config/chains';
import { withFallback } from '@sdk/utils';
import { SdkEnvironmentVars } from '..';

// Private keys for different chains

export type KYCStatus = 'pending' | 'approved' | 'rejected' | null;

export type KYC = {
  country: string;
  currency: string;
  kycStatus: KYCStatus;
  updatedAt: string;
  fullName: string;
};

/**
 * Parameters for the authenticate method
 */
export interface AuthenticateParams {
  /** WalletClient instance for authentication */
  walletClient?: WalletClient;
  /** EIP1193Provider instance for authentication */
  provider?: EIP1193Provider | EthereumProvider;
  /** Optional JWT token to use instead of SIWE */
  jwt?: string;
}

/**
 * Auth class for SDK usage. Wraps all authentication logic.
 */
export class Auth {
  /**
   * The current JWT token, or null if not authenticated.
   */
  private token: string | null = null; // user JWT
  public adminToken: string | null = null; // admin JWT

  public status: 'authenticated' | 'unauthenticated' | 'wallet_connected' | 'loading' =
    'unauthenticated';

  /**
   * The current WalletClient instance, or null if not set.
   */
  private extendedWalletClient: ExtendedWalletClient | null = null;
  /**
   * The original EIP1193Provider instance, or null if not set.
   * Stored to enable connection for WalletConnect providers during authentication.
   */
  private originalProvider: EIP1193Provider | EthereumProvider | null = null;
  /**
   * The current wallet address, or null if not set.
   */
  private account: BackendAccount | null = null;
  public kyc: KYC | null = null;
  public chains: IxChain[];
  private settings: SettingsService | null = null;
  public autoTrader: AutoTraderService;

  public get adminAxios(): AxiosInstance {
    if (!this.adminToken) {
      throw new Error('Admin token is not set');
    }
    return createProtectedAxios(this.adminToken, this.adminBaseUrl);
  }

  /**
   * @param supportedChains Supported EVM chains
   * @param notification    Notification client instance
   * @param baseUrl         Base URL for **user** API endpoints
   * @param defaultChainId  Fallback chain id for unsupported networks
   * @param publicAxios     Axios instance pointed at the `baseUrl`
   * @param adminBaseUrl    Optional base URL for **admin** API endpoints. Falls back to `baseUrl` when not provided.
   */
  constructor(
    supportedChains: IxChain[],
    public notification: NotificationClient,
    private baseUrl: string,
    private defaultChainId: number,
    public publicAxios: AxiosInstance,
    private environment: SdkEnvironmentVars,
  ) {
    this.chains = supportedChains;
    // If a dedicated admin URL is provided, use it – otherwise default to the regular base URL
    this.adminBaseUrl = baseUrl; // Until the admin token is set we create a PUBLIC axios instance pointed at the admin base URL.
    // Initialize AutoTrader service
    this.autoTrader = new AutoTraderService(this);
  }

  /**
   * Separate base URL for admin endpoints (may point to a different env).
   */
  private readonly adminBaseUrl: string;

  // Get nonce from server
  public getNonce = async (): Promise<string> => {
    try {
      const res = await this.publicAxios.get(`/auth/siwe/nonce`);
      return res.data;
    } catch (error) {
      console.error('Failed to get nonce:', error);
      throw error;
    }
  };

  // Create SIWE message
  public createMessage = (nonce: string, address: string, chainId: number): SiweMessage => {
    // You may want to set domain/uri to window.location.host/origin in browser
    const domain = typeof window !== 'undefined' ? window.location.host : 'localhost';
    const uri = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    return new SiweMessage({
      domain,
      address,
      statement: 'Sign in with Ethereum to the app.',
      uri,
      version: '1',
      chainId,
      nonce,
    });
  };

  // Convert message object to a string that can be signed
  private getMessageBody = (message: SiweMessage): string => message.prepareMessage();

  // Verify SIWE signature with server
  public verify = async (message: string, signature: string): Promise<string> => {
    try {
      const res = await this.publicAxios.post(`/auth/siwe/verify`, {
        message,
        signature,
      });
      if (!res.data || !res.data.token) {
        throw new Error('Verification failed - no token returned');
      }
      return res.data.token;
    } catch (error) {
      console.error('Verification failed:', error);
      throw error;
    }
  };

  public get protectedAxios(): AxiosInstance {
    return createProtectedAxios(this.token as string, this.baseUrl);
  }

  /**
   * Authenticates with SIWE using a private key from environment variables
   * @param wallet The WalletClient instance
   * @param address The wallet address
   * @returns Authentication token if successful
   */
  private authenticateSiwe = async (
    wallet: WalletClient,
    address: `0x${string}`,
  ): Promise<string | null> => {
    const account = wallet.account;
    if (!account) {
      throw new Error(
        'Wallet address is not set. Please ensure the Wallet client was created with an account',
      );
    }
    let chainId = await wallet.getChainId();

    this.notification.notify({
      type: IxEventType.SIWE_AUTHENTICATING,
      data: {
        address: address,
        currentChainId: chainId,
      },
    });

    const nonce = await this.getNonce();

    const isChainSupported = this.chains.some(chain => chain.id === chainId);

    if (!isChainSupported) {
      chainId = this.defaultChainId;
      console.warn('Unsupported network. Using default chain', chainId);
      this.notification.notify({
        type: IxEventType.UNSUPPORTED_NETWORK,
        data: {
          currentChainId: chainId,
          address: address,
        },
      });
    }
    const message = this.createMessage(nonce, address, chainId);
    const signature = await wallet.signMessage({
      message: this.getMessageBody(message),
      account: account,
    });
    return this.verify(this.getMessageBody(message), signature);
  };

  /**
   * Helper function to detect if a provider is WalletConnect-like by checking for connect method
   */
  private isWalletConnectProvider(provider: EIP1193Provider | EthereumProvider): boolean {
    return (provider as EthereumProvider).isWalletConnect;
  }

  /**
   * Store the provided WalletClient instance, extending it with viem public actions.
   * If the client already contains an Account instance, update the auth status to "wallet_connected".
   */
  public setWalletClient = async (walletClient: WalletClient): Promise<ExtendedWalletClient> => {
    // Clear original provider when setting wallet client directly

    const client = walletClient.extend(publicActions);
    if (walletClient.account?.type === 'local') {
      // If the client already has an Account attached, update status
      this.status = this.status !== 'authenticated' ? 'wallet_connected' : this.status;
      this.extendedWalletClient = client;
      this.notification.notify({
        type: IxEventType.WALLET_CONNECTED,
        data: {
          address: walletClient.account.address,
          currentChainId: walletClient.chain?.id || 0,
        },
      });
      return client;
    }
    const [address] = await client.request({ method: 'eth_accounts' }).catch(error => {
      console.warn('Failed to get accounts in setWalletClient:', error);
      return [];
    });
    if (address) {
      const addressHex = checksumAddress(address);
      client.account = toAccount(addressHex);
    }
    if (!client.chain) {
      client.chain = this.chains.find(chain => chain.id === this.defaultChainId);
    }
    this._activeChain = this.chains.find(chain => chain.id === client.chain?.id) || null;

    this.extendedWalletClient = client;
    return client;
  };

  /**
   * Store the provided EIP1193Provider instance, converting it to a WalletClient.
   * Does not attempt to connect - only tries to request accounts once.
   */
  public setProvider = async (provider: EIP1193Provider | EthereumProvider): Promise<void> => {
    let walletClient: WalletClient;

    try {
      // Store the original provider for potential connection during authentication
      this.originalProvider = provider;

      // Create WalletClient from provider
      walletClient = createWalletClient({
        transport: custom(provider),
        chain: this.chains.find(chain => chain.id === this.defaultChainId),
      });

      // Try to get accounts (but don't force connection)
      try {
        const [address] = await walletClient.request({ method: 'eth_requestAccounts' });
        if (address) {
          const addressHex = checksumAddress(address);
          walletClient.account = toAccount(addressHex);
        }
      } catch (accountsError: any) {
        // For any error, just create wallet client without account for now
        // Authentication can handle the connection flow later
        console.warn('Failed to get accounts in setProvider:', accountsError);
      }

      // Use setWalletClient to complete the setup
      await this.setWalletClient(walletClient);
    } catch (error) {
      throw new Error(`Failed to set provider: ${error}`);
    }
  };

  /**
   * Get the current WalletClient (if set).
   * @returns The WalletClient instance or null
   */
  getWalletClient = (): ExtendedWalletClient | null => {
    return this.extendedWalletClient;
  };

  getProvider = (): EIP1193Provider | EthereumProvider | null => {
    return this.originalProvider;
  };

  /**
   * Get the current address (if set).
   * @returns The wallet address or null
   */
  getAddress = (): `0x${string}` | null => {
    return this.getWalletClient()?.account?.address || null;
  };

  extractKyc = (BackendAccount: BackendAccount) => {
    const submitedKycs = BackendAccount.addresses.find(ad => ad.kycManual?.length > 0)?.kycManual;
    const latestKYC = submitedKycs?.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
    const status: KYCStatus = latestKYC?.kycValidatedAt
      ? 'approved'
      : latestKYC?.kycRejectedAt
      ? 'rejected'
      : latestKYC
      ? 'pending'
      : null;

    return (
      status && {
        country: latestKYC?.country || '',
        currency: latestKYC?.currency || '',
        kycStatus: status,
        updatedAt: latestKYC?.updatedAt || '',
        fullName: `${latestKYC?.firstName} ${latestKYC?.otherName} ${latestKYC?.lastName}`,
      }
    );
  };

  fetchKycStatus = async (jwt: string): Promise<KYC | null> => {
    try {
      const res = await this.settings?.getAccount(jwt);
      return (res?.data && this.extractKyc(res?.data)) || null;
    } catch (e) {
      return null;
    }
  };

  /**
   * Authenticate using SIWE and store the JWT token, or use provided JWT.
   * @param params Authentication parameters containing walletClient, provider, and/or jwt
   * @returns The JWT token if successful
   */
  public authenticate = async (params: AuthenticateParams = {}): Promise<string> => {
    try {
      const { walletClient: providedWalletClient, provider: providedProvider, jwt } = params;

      // Determine the wallet client to use
      let walletClient: WalletClient;

      // Check if we can use an already connected wallet
      if (!providedWalletClient && !providedProvider && this.extendedWalletClient) {
        // Wallet already connected – use stored client
        walletClient = this.extendedWalletClient;
      } else {
        // Validate that at least one authentication method is provided
        if (!providedWalletClient && !providedProvider) {
          throw new Error(
            'Either walletClient or provider must be provided when no wallet is connected',
          );
        }

        // Prioritize provider if both are provided
        if (providedProvider) {
          await this.setProvider(providedProvider);
          walletClient = this.extendedWalletClient!;
        } else if (providedWalletClient) {
          await this.setWalletClient(providedWalletClient);
          walletClient = providedWalletClient;
        } else {
          throw new Error('Invalid state: should not reach here');
        }
      }

      // Ensure we have an account associated with the wallet
      if (!walletClient.account) {
        // Try to get accounts first
        if (this.originalProvider && 'isWalletConnect' in this.originalProvider) {
          try {
            // Connect to WalletConnect provider and retry account request
            await this.originalProvider.connect();
          } catch (connectError) {
            console.error('Failed to connect to WalletConnect provider:', connectError);
            throw connectError;
          }
        }
        const [_address] = await walletClient.request({ method: 'eth_requestAccounts' });
        const addressHex = checksumAddress(_address);
        walletClient.account = toAccount(addressHex);
      }
      if (walletClient.account.type === 'json-rpc') {
        const [address] = await walletClient.request({ method: 'eth_requestAccounts' });
        const walletChainId = await walletClient.getChainId();
        const walletChain = this.chains.find(c => c.id === walletChainId);

        if (walletChain) {
          walletClient.switchChain({ id: walletChain.id });
        } else {
          await this.switchChain(this.defaultChainId);
        }

        if (address) {
          const addressHex = checksumAddress(address);
          walletClient.account = toAccount(addressHex);
        }
      }
      const chainId = await walletClient.getChainId();
      const supportedChain = this.chains.find(chain => chain.id === chainId);
      if (!supportedChain) {
        console.warn('Unsupported network', chainId);
        this.notification.notify({
          type: IxEventType.UNSUPPORTED_NETWORK,
          data: {
            currentChainId: chainId,
            address: walletClient.account.address,
          },
        });
      }

      const extendedWalletClient = await this.setWalletClient(walletClient);
      if (!walletClient.chain) {
        walletClient.chain = supportedChain;
      }

      this.status = 'loading';

      const address = walletClient.account.address as `0x${string}`;

      this.token = jwt || (await this.authenticateSiwe(walletClient, address));

      if (!this.token) {
        throw new Error('Failed to get authentication token');
      }

      this.settings = new SettingsService(this.protectedAxios, this.publicAxios);
      const { data: account } = await this.settings.getAccount(this.token);
      this.account = account;

      this.kyc = account && this.extractKyc(account);
      this.notification.connect({
        channelName: address.toLowerCase(),
        onConnect: () => {
          // Notification service connected
        },
        onDisconnect: () => {
          // Notification service disconnected
        },
      });

      const currentActiveChainId = await this.getWalletClient()?.getChainId();
      const currentActiveChain = this.chains.find(c => c.id === currentActiveChainId);
      if (!currentActiveChain) {
        this.notification.notify({
          type: IxEventType.UNSUPPORTED_NETWORK,
          data: {
            currentChainId: currentActiveChainId || 0,
            address: address,
          },
        });
      } else {
        this._activeChain = currentActiveChain;
      }
      this.status = 'authenticated';
      this.notification.notify({
        type: IxEventType.AUTHENTICATED,
        data: {
          account: account,
          chainId: currentActiveChainId || null,
          walletClient: extendedWalletClient,
        },
      });
      return this.token;
    } catch (error) {
      // Reset auth state on failure
      this.status = 'unauthenticated';
      // Propagate the error for caller handling
      throw error;
    }
  };

  /**
   * Get the current JWT token (if authenticated).
   * @returns The JWT token or null
   */
  getToken = (): string | null => {
    return this.token;
  };

  /**
   * Directly inject a pre-existing JWT (e.g., from env vars or cookie).
   */
  public setAuthToken = (token: string): void => {
    if (!token) throw new Error('Token must be provided');
    this.token = token;
  };

  /** Inject an existing JWT to be used for admin endpoints. */
  public setAdminToken = (token: string): void => {
    if (!token) throw new Error('Token must be provided');
    this.adminToken = token;
  };

  /* -------------------------------------------------------------------------- */
  /*                           ADMIN AUTHENTICATION                             */
  /* -------------------------------------------------------------------------- */

  /**
   * Send a one-time login code to the admin e-mail address.
   * Mirrors POST /admin/auth endpoint used by the web admin dashboard.
   */
  public adminSendLoginCode = async (email: string): Promise<void> => {
    if (!email) throw new Error('Email is required');
    await this.publicAxios.post('/admin/auth', { email });
  };

  /**
   * Verify the admin code and store the returned JWT token.
   * Mirrors POST /admin/auth/verify endpoint.
   * After verification the SDK is considered authenticated and
   * `protectedAxios` is ready for admin endpoints.
   */
  public adminVerifyCode = async (email: string, code: number | string): Promise<string> => {
    if (!email) throw new Error('Email is required');
    if (!code) throw new Error('Code is required');
    const numericCode = typeof code === 'string' ? parseInt(code) : code;
    const res = await this.publicAxios.post('/admin/auth/verify', {
      email,
      code: numericCode,
    });
    if (!res.data?.token) {
      throw new Error('Admin verification failed – no token returned');
    }

    this.adminToken = res.data.token;
    // Do not set global status – admin auth is independent of user SIWE state
    return this.adminToken as string;
  };

  /**\n   * Currently active chain for on-chain operations. Updated via switchChain().\n   */
  private _activeChain: IxChain | null = null;

  public getActiveChain = () => {
    console.log('getActiveChain', this._activeChain);
    return this._activeChain;
  };

  /**
   * Switch the connected wallet (if possible) and SDK state to the specified chain.
   * For embedded Json-RPC accounts ("json-rpc" type) switching is skipped because
   * the provider already targets the desired network.
   *
   * @param chainOrId Chain object or EVM chain id to switch to.
   * @throws Error when the chain is not supported or the wallet rejects the switch.
   */
  public switchChain = async (chainId: number | null): Promise<IxChain | null> => {
    if (chainId === null) {
      this._activeChain = null;
      return null;
    }
    const targetChain: IxChain | undefined = this.chains.find(c => c.id === chainId);

    if (targetChain === undefined) {
      console.warn(`Unsupported chain id ${chainId}`, this.chains);
    }

    // Attempt to switch via the connected wallet when  using a local Json-RPC account
    const walletClient = this.getWalletClient();
    if (
      walletClient &&
      walletClient.account?.type === 'json-rpc' &&
      typeof walletClient.switchChain === 'function'
    ) {
      if (targetChain) {
        await walletClient.switchChain({ id: targetChain.id }).catch(async e => {
          console.warn('Error switching chain', e);
          await walletClient.addChain({ chain: targetChain });
          await walletClient.switchChain({ id: targetChain.id });
        });
      }
      // Ensure the wallet client reflects the new chain
      walletClient.chain = targetChain;
    }
    if (walletClient?.account?.type === 'local') {
      const wallet = createWalletClient({
        account: walletClient.account,
        chain: targetChain,
        transport: http(),
      });
      this.setWalletClient(wallet);
    }

    // Update SDK-level active chain reference
    if (this.getProvider()) {
      const provider = this.getProvider()!;
      if (this.isWalletConnectProvider(provider)) {
        try {
          await (provider as EthereumProvider).request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: `0x${chainId?.toString(16)}` }],
          });
        } catch (switchError) {
          console.warn('Failed to switch chain on provider:', switchError);
        }
      }
    }
    this.notification.notify({
      type: IxEventType.CHAIN_SWITCHED,
      data: {
        previousChainId: this._activeChain?.id || null,
        currentChainId: chainId,
        address: this.getAddress() || '',
      },
    });
    this._activeChain = targetChain || null;
    return targetChain || null;
  };

  /**
   * Log out and clear the token and wallet info.
   */
  public signOut = async (): Promise<void> => {
    try {
      this.extendedWalletClient?.request({ method: 'wallet_disconnect' }).catch(() => {});
      this.token = null;
      this.kyc = null;
      this.account = null;
      this.status = 'unauthenticated';
      this.notification.cleanup();
    } catch (e) {
      console.warn('Signout request failed', e);
    }
  };
}
